import { APPOINTMENT_SLOT_MINUTES } from '@tailoring-crm/shared';
import { APP_TIME_ZONE, toDate, type DateInput } from '../format.js';

/**
 * Every instant on the appointments screen is rendered and constructed in the shop's own
 * timezone, never the browser's. A booking made at half past midnight in Kolkata must land on
 * THAT day's column even when the browser reasons in UTC — which is the entire reason this
 * module exists rather than the arithmetic living inline in the grid component.
 *
 * The trick used throughout: read the shop-local calendar date out of a real instant with
 * `Intl.DateTimeFormat`, then do every further day-counting calculation on a plain UTC `Date`
 * built from THAT date string. That second `Date` is never a real instant — it is a calendar
 * counter — so `getUTCDay` / `setUTCDate` give correct weekday and day-count arithmetic without
 * the browser's own timezone ever entering the picture a second time.
 *
 * Building a real instant back out of a shop-local day and time (`slotInstant`) uses the fixed
 * `+05:30` offset directly: India has one timezone and no daylight saving, so there is no
 * ambiguity a lookup table would be needed to resolve.
 */

function shopDateParts(value: DateInput): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(toDate(value));
  const get = (type: string): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  return { year: get('year'), month: get('month'), day: get('day') };
}

function shopTimeParts(value: DateInput): { hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: APP_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(toDate(value));
  const get = (type: string): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  return { hour: get('hour'), minute: get('minute') };
}

function dayKeyOfParts({ year, month, day }: { year: number; month: number; day: number }): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** A pure calendar counter, never a real instant — see the module note above. */
function calendarDate(dayKey: string): Date {
  return new Date(`${dayKey}T00:00:00Z`);
}

function dayKeyOfCalendar(date: Date): string {
  return dayKeyOfParts({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  });
}

/** The Monday of the week containing an instant, as `'YYYY-MM-DD'` in `APP_TIME_ZONE`. */
export function mondayOf(value: DateInput): string {
  const date = calendarDate(dayKeyOfParts(shopDateParts(value)));
  const weekday = date.getUTCDay(); // 0 Sunday .. 6 Saturday
  const daysSinceMonday = weekday === 0 ? 6 : weekday - 1;
  date.setUTCDate(date.getUTCDate() - daysSinceMonday);
  return dayKeyOfCalendar(date);
}

/** The Monday `delta` weeks away from `weekStartDate` — negative steps back. */
export function shiftWeek(weekStartDate: string, delta: number): string {
  const date = calendarDate(weekStartDate);
  date.setUTCDate(date.getUTCDate() + delta * 7);
  return dayKeyOfCalendar(date);
}

/** The seven `'YYYY-MM-DD'` day keys of the week, Monday first. */
export function weekDays(weekStartDate: string): string[] {
  const start = calendarDate(weekStartDate);
  return Array.from({ length: 7 }, (_unused, index) => {
    const date = new Date(start);
    date.setUTCDate(date.getUTCDate() + index);
    return dayKeyOfCalendar(date);
  });
}

/**
 * The ISO instant for a grid cell: `dayKey` at `'HH:mm'`, half-hour aligned, in `APP_TIME_ZONE`.
 * Built with the fixed `+05:30` offset — see the module note.
 */
export function slotInstant(dayKey: string, startTime: string): string {
  return new Date(`${dayKey}T${startTime}:00.000+05:30`).toISOString();
}

/** Which day column an instant falls in, as `'YYYY-MM-DD'` in `APP_TIME_ZONE`. */
export function dayKeyOf(value: DateInput): string {
  return dayKeyOfParts(shopDateParts(value));
}

/** Which grid row an instant falls in, as `'HH:mm'` in `APP_TIME_ZONE`. */
export function slotKeyOf(value: DateInput): string {
  const { hour, minute } = shopTimeParts(value);
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** How many grid rows a booking of this length covers. */
export function slotSpan(durationMinutes: number): number {
  return Math.max(1, Math.round(durationMinutes / APPOINTMENT_SLOT_MINUTES));
}
